import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SublyApi from '../../../../helpers/Api';
import serviceIcons from '../../../../helpers/serviceIcons';
import ConfirmationDialog from './ConfirmationDialog';

const ServiceCard = ({ subscription, price, setLoading }) => {
	const token = useSelector((st) => st.user.token);
	const currentUser = useSelector((st) => st.user.currentUser);
	const [dialogOpen, setDialogOpen] = useState(false);

	const isCanceled = subscription.cancel_at_period_end;
	const title = price?.product?.name || subscription.productName || 'Subscription';
	const subtitle = price?.nickname || '';
	const monthlyPrice = price
		? price.unit_amount / 100
		: (subscription.items?.data?.[0]?.price?.unit_amount || 0) / 100;
	const description = price?.product?.description || 'This is a fantastic Jane Rothe Product';
	const iconKey = price?.product?.metadata?.icon;
	const icon = iconKey ? serviceIcons.icons[iconKey] : null;

	// Get subscription status
	const getStatusBadge = () => {
		if (isCanceled) {
			return (
				<div className='flex items-center space-x-1 text-orange-600 bg-orange-50 px-2 py-1 rounded-full text-xs font-montserrat-medium'>
					<FiAlertCircle className='h-3 w-3' />
					<span>Canceling</span>
				</div>
			);
		}

		switch (subscription.status) {
			case 'active':
				return (
					<div className='flex items-center space-x-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs font-montserrat-medium'>
						<FiCheck className='h-3 w-3' />
						<span>Active</span>
					</div>
				);
			case 'canceled':
				return (
					<div className='flex items-center space-x-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs font-montserrat-medium'>
						<FiX className='h-3 w-3' />
						<span>Canceled</span>
					</div>
				);
			default:
				return (
					<div className='flex items-center space-x-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs font-montserrat-medium'>
						<FiAlertCircle className='h-3 w-3' />
						<span>{subscription.status || 'Unknown'}</span>
					</div>
				);
		}
	};

	const handleManageClick = () => {
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
	};

	const handleDialogConfirmation = async () => {
		try {
			setLoading(true);
			setDialogOpen(false);

			// Get current URL for return path
			const currentUrl = window.location.href;

			const portalSessionUrl = await SublyApi.customerPortal(
				currentUser.customerId,
				token,
				currentUrl
			);
			window.location.href = portalSessionUrl;
		} catch (err) {
			console.error('Error accessing customer portal:', err);
			setLoading(false);
		}
	};

	const getModalText = () => {
		return isCanceled
			? "You've already unsubscribed from this service. Want to see the dashboard anyway?"
			: `Manage your ${title} subscription in the Stripe customer portal.`;
	};

	return (
		<>
			<div className='bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 flex flex-col h-full'>
				{/* Icon Section */}
				<div className='p-5'>
					<div className='flex items-start justify-between mb-3'>
						{icon && (
							<div className={`${isCanceled ? 'bg-gray-100' : 'bg-[#CC1DB9]'} rounded-lg flex items-center justify-center transition-colors w-12 h-12`}>
								<span className='text-xl leading-none flex items-center justify-center'>{icon}</span>
							</div>
						)}
						{getStatusBadge()}
					</div>

					{/* Service Info */}
					<h3 className='text-lg font-glacial text-gray-900 mb-1.5'>{title}</h3>
					{subtitle && (
						<p className='text-sm font-montserrat-regular text-gray-600 mb-2.5'>{subtitle}</p>
					)}

					{/* Price */}
					<div className='mb-2.5'>
						<p className='text-2xl font-glacial text-gray-900'>
							${monthlyPrice.toFixed(2)}
							<span className='text-sm font-montserrat-regular text-gray-500'>/month</span>
						</p>
					</div>

					<p className='text-sm font-montserrat-regular text-gray-500 line-clamp-2'>
						{description}
					</p>
				</div>

				{/* Action Button */}
				<div className='px-5 pb-5 mt-auto'>
					<button
						onClick={handleManageClick}
						className={`w-full py-2.5 px-4 rounded-lg font-montserrat-medium text-sm transition-colors ${
							isCanceled
								? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
								: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
						}`}
					>
						{isCanceled ? 'View in Portal' : 'Manage Subscription'}
					</button>
				</div>
			</div>

			<ConfirmationDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				onConfirm={handleDialogConfirmation}
				message={getModalText()}
				confirmText="Open Portal"
				cancelText="Cancel"
			>
				{!isCanceled && (
					<p className="mt-3 text-xs text-gray-400 font-montserrat-regular">
						Use the return link in the sidebar when done.
					</p>
				)}
			</ConfirmationDialog>
		</>
	);
};

export default ServiceCard;